import {createContext, ReactChild, useContext} from 'react';
import {Entity, fetchUserPermissions} from './api';
import {useQuery} from 'react-query';
import {useClientId} from '../../../hooks/useClientId';
import axios from 'axios';
import {useNativeAuth} from '../native/NativeAuthProvider';

interface PermissionsContextProps {
  children: ReactChild;
}

const PermissionContext = createContext<string[]>([]);
const EntitiesContext = createContext<Entity[]>([]);
const ImpersonateContext = createContext<string | null>(null);
const CurrentCustomerContext = createContext<App.Domains.Auth.Dto.Customer | null>(null);
const ImpersonateFirstNameContext = createContext<string | null>(null);

const usePermissions = () => useContext(PermissionContext);
const useEntities = () => useContext(EntitiesContext);

const useImpersonate = () => useContext(ImpersonateContext);
const useImpersonateFirstName = () => useContext(ImpersonateFirstNameContext);

// Client sur lequel l'utilisateur est focalisé (« se connecter en tant que »), null = tous ses clients
const useCurrentCustomer = () => useContext(CurrentCustomerContext);

const PermissionProvider = ({children}: PermissionsContextProps): JSX.Element => {
  const clientId = useClientId();
  const {token: nativeToken} = useNativeAuth();

  const bearer = nativeToken;

  const useFetchUserPermissions = () => {
    return useQuery({
      queryKey: ['userPermissions', clientId, bearer],
      queryFn: () => {
        if (!bearer) {
          throw new Error('No token');
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${bearer}`;
        return fetchUserPermissions(bearer);
      },
      enabled: !!bearer,
      retry: 2,
    });
  };

  const {data: userPermissions, isLoading, isError, isIdle} = useFetchUserPermissions();

  // A transient failure to fetch permissions must not be treated as "no permissions" —
  // SecuredRoute can't tell that apart from a genuine denial and would hard-redirect
  // to /error/401, wiping the requested URL from browser history.
  if (isLoading || isIdle || isError) {
    return <></>;
  }

  return (
    <PermissionContext.Provider value={userPermissions.permissions}>
      <EntitiesContext.Provider value={userPermissions.entities}>
        <ImpersonateContext.Provider value={userPermissions?.impersonate ?? null}>
          <ImpersonateFirstNameContext.Provider
            value={userPermissions?.impersonateFirstName ?? null}
          >
            <CurrentCustomerContext.Provider value={userPermissions?.currentCustomer ?? null}>
              {children}
            </CurrentCustomerContext.Provider>
          </ImpersonateFirstNameContext.Provider>
        </ImpersonateContext.Provider>
      </EntitiesContext.Provider>
    </PermissionContext.Provider>
  );
};

export {
  PermissionProvider,
  usePermissions,
  useEntities,
  useImpersonate,
  useImpersonateFirstName,
  useCurrentCustomer,
};
